import React, { useState } from "react";
import { connect } from "react-redux";
import {
  getWidgetSpecificIsVisible,
  getWidgetSpecificProps
} from "@utils/index";
import CompletedIcon from "@public/images/icons/completed.svg";
import NotCompletedIcon from "@public/images/icons/not-completed.svg";
import {
  Container,
  Title,
  TimelineList,
  TimelineItem,
  IconWrapper,
  ContentWrapper,
  StatusText,
  DateTimeText,
  ConnectorLine,
  SeparatorLine,
  MarkAsDeliveredPrompt,
  MarkAsDeliveredLink
} from "./styles";

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    ),
    ...getWidgetSpecificProps(state.configuration?.normalisedConfig, ownProps.id)
  };
};

interface OrderStatus {
  status: string;
  heading: string;
  subHeading: string;
  isCompleted: string | boolean;
  imgSrc: string;
  statusUpdatedOn: string;
}

const isTrue = (value: unknown) => value === true || value === "true";

const normalizedStatus = (status?: string) => (status || "").trim().toLowerCase();

const DeliveryTimeline = (props: any) => {
  const {
    is_visible,
    delivery_status = [],
    title,
    actions,
    show_mark_as_delivered,
    order_id,
    mark_as_delivered_prompt,
    mark_as_delivered_cta
  } = props;
  const [isMarkingDelivered, setIsMarkingDelivered] = useState(false);

  if (!is_visible) return <></>;

  const isCompleted = (completed: string | boolean) => {
    if (typeof completed === "string") {
      return completed.toLowerCase() === "true";
    }
    return Boolean(completed);
  };

  const isAlreadyDelivered = delivery_status.some(
    (item: OrderStatus) =>
      normalizedStatus(item.status) === "delivered" && isCompleted(item.isCompleted)
  );

  const handleMarkAsDelivered = async () => {
    if (isMarkingDelivered) return;

    setIsMarkingDelivered(true);
    try {
      for (const action of actions?.on_click || []) {
        await action.funCall?.(action.args);
      }
    } catch {
      // The SDUI action displays the failure toast and restores the basic loader.
    } finally {
      setIsMarkingDelivered(false);
    }
  };

  return (
    <Container>
      <Title>{title}</Title>
      <TimelineList>
        {delivery_status.map((item: OrderStatus, index: number) => {
          const completed = isCompleted(item.isCompleted);
          const isLast = index === delivery_status.length - 1;
          const canMarkAsDelivered =
            isTrue(show_mark_as_delivered) &&
            Boolean(order_id) &&
            !isAlreadyDelivered &&
            normalizedStatus(item.status) === "out for delivery";

          return (
            <React.Fragment key={index}>
              <TimelineItem>
                <IconWrapper>
                  {completed ? (
                    <CompletedIcon width={20} height={20} />
                  ) : (
                    <NotCompletedIcon width={20} height={20} />
                  )}
                  {!isLast && (
                    <ConnectorLine isCompleted={completed} />
                  )}
                </IconWrapper>
                <ContentWrapper>
                  <StatusText>{item.status}</StatusText>
                  {canMarkAsDelivered ? (
                    <>
                      {mark_as_delivered_prompt && (
                        <MarkAsDeliveredPrompt>
                          {mark_as_delivered_prompt}
                        </MarkAsDeliveredPrompt>
                      )}
                      {mark_as_delivered_cta && (
                        <MarkAsDeliveredLink
                          type="button"
                          disabled={isMarkingDelivered}
                          aria-busy={isMarkingDelivered}
                          onClick={handleMarkAsDelivered}
                        >
                          {mark_as_delivered_cta}
                        </MarkAsDeliveredLink>
                      )}
                    </>
                  ) : (
                    <DateTimeText>
                      {item.statusUpdatedOn || "---"}
                    </DateTimeText>
                  )}
                </ContentWrapper>
              </TimelineItem>
              {!isLast && <SeparatorLine />}
            </React.Fragment>
          );
        })}
      </TimelineList>
    </Container>
  );
};

export default connect(mapStateToProps)(DeliveryTimeline);

